import React from 'react';
import { useTranslation } from 'react-i18next';
import GButton from '../../components/ui/base/GButton';
import type { SupportTicket } from './index';

interface TicketListProps {
    tickets: SupportTicket[];
    loading: boolean;
    onSelect: (id: number) => void;
    onCreateNew: () => void;
}

const STATUS_COLORS: Record<string, string> = {
    OPEN: 'bg-[#4DBDFF]/15 text-[#4DBDFF] border-[#4DBDFF]/20',
    IN_PROGRESS: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/20',
    RESOLVED: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
    CLOSED: 'bg-white/[0.06] text-white/40 border-white/[0.08]',
    PENDING: 'bg-orange-500/15 text-orange-400 border-orange-500/20',
};

const PRIORITY_COLORS: Record<string, string> = {
    LOW: 'text-white/30',
    MEDIUM: 'text-yellow-400',
    HIGH: 'text-orange-400',
    URGENT: 'text-red-400',
};

const CATEGORY_LABELS: Record<string, string> = {
    GENERAL: 'General',
    AUTHENTICATION: 'Authentication',
    PAYMENT: 'Payment',
    DEPOSIT: 'Deposit',
    WITHDRAWAL: 'Withdrawal',
    BONUS: 'Bonus',
    GAMES: 'Games',
    ACCOUNT: 'Account',
    TECHNICAL: 'Technical',
    OTHER: 'Other',
};

const formatDate = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const TicketList: React.FC<TicketListProps> = ({ tickets, loading, onSelect, onCreateNew }) => {
    const { t } = useTranslation();

    if (loading && tickets.length === 0) {
        return (
            <div className="space-y-[12px]">
                {[1, 2, 3].map((i) => (
                    <div key={i} className="rounded-[16px] bg-white/[0.03] border border-white/[0.06] p-[20px] animate-pulse">
                        <div className="h-[14px] bg-white/[0.06] rounded w-1/4 mb-[12px]"></div>
                        <div className="h-[18px] bg-white/[0.06] rounded w-3/4 mb-[8px]"></div>
                        <div className="h-[12px] bg-white/[0.04] rounded w-1/2"></div>
                    </div>
                ))}
            </div>
        );
    }

    if (!loading && tickets.length === 0) {
        return (
            <div className="rounded-[20px] bg-white/[0.03] border border-white/[0.06] p-[40px] flex flex-col items-center gap-[20px]">
                <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
                     className="text-white/10">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                </svg>
                <div className="text-center">
                    <h3 className="text-white text-[18px] font-bold mb-[6px]">{t('support.noTickets')}</h3>
                    <p className="text-white/30 text-[13px] font-medium">{t('support.noTicketsDesc')}</p>
                </div>
                <GButton variant="primary" className="py-[10px] px-[24px] rounded-[12px]" onClick={onCreateNew}>
                    {t('support.createFirst')}
                </GButton>
            </div>
        );
    }

    return (
        <div className="space-y-[10px]">
            {tickets.map((ticket) => (
                <button
                    key={ticket.id}
                    onClick={() => onSelect(ticket.id)}
                    className="w-full text-left rounded-[16px] bg-white/[0.03] border border-white/[0.06] hover:border-[#4DBDFF]/20 hover:bg-white/[0.05] transition-all duration-300 p-[16px] md:p-[20px] group"
                >
                    <div className="flex flex-col md:flex-row md:items-center gap-[10px] md:gap-[20px]">
                        {/* Left: title + meta */}
                        <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-[8px] mb-[4px]">
                                <span className="text-white/20 text-[11px] font-semibold">#{ticket.id}</span>
                                <span className="text-white/10">·</span>
                                <span className="text-white/30 text-[11px] font-medium">
                                    {CATEGORY_LABELS[ticket.category] ?? ticket.category}
                                </span>
                            </div>
                            <h3 className="text-white text-[15px] font-bold truncate group-hover:text-[#4DBDFF] transition-colors duration-200">
                                {ticket.title}
                            </h3>
                            <p className="text-white/25 text-[12px] font-medium mt-[3px] truncate">
                                {ticket.description}
                            </p>
                        </div>

                        {/* Right: badges */}
                        <div className="flex items-center gap-[8px] flex-shrink-0">
                            {ticket.priority && (
                                <span className={`text-[11px] font-bold uppercase tracking-wider ${PRIORITY_COLORS[ticket.priority] ?? 'text-white/30'}`}>
                                    {ticket.priority}
                                </span>
                            )}
                            <span className={`text-[11px] font-semibold px-[8px] py-[3px] rounded-full border ${STATUS_COLORS[ticket.status] ?? STATUS_COLORS.OPEN}`}>
                                {ticket.status?.replace('_', ' ')}
                            </span>
                            {ticket.comments?.length > 0 && (
                                <span className="flex items-center gap-[4px] text-white/25 text-[11px] font-medium">
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                                         strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                                    </svg>
                                    {ticket.comments.length}
                                </span>
                            )}
                            <span className="text-white/15 text-[11px] font-medium hidden md:block">
                                {formatDate(ticket.createdAt)}
                            </span>
                        </div>
                    </div>
                </button>
            ))}
        </div>
    );
};

export default TicketList;
